/*
 * Flex Recordings, js/audio.js
 * ------------------------------------------------------------------------
 * Bandcamp stream tokens expire, so releases.json intentionally carries NO
 * direct stream URLs. Real playback today happens through the Bandcamp
 * iframe embed in the release modal (see app.js), or via a per-release /
 * per-track `audioUrl` when Flex self-hosts a file or uses a CDN.
 *
 * FlexAudio therefore NEVER touches its single shared <audio> element unless
 * a direct URL exists, the iframe owns its own playback independently.
 *
 * Singleton API (window.FlexAudio):
 *   loadAndPlay(release, trackIndex|null) -> Promise({ ok:boolean })
 *       false when no direct audio URL exists anywhere (release.audioUrl or
 *       tracks[i].audioUrl); otherwise sets current release/track, plays,
 *       resets. Rapid calls are guarded, only the newest load wins.
 *   toggle()      play/pause current (no-op if nothing loaded)
 *   seekRatio(r)  seek to 0..1 of duration (ignored if nothing loaded)
 *   stop()        pause + clear + emit state
 *   on(event, cb) "time"   -> { current, duration, ratio }  on timeupdate
 *                 "state"  -> { playing, release, trackIndex } on play/pause/
 *                             ended/load (and after stop/loadAndPlay reset)
 *                 "error"  -> { release, message }
 *       returns an unsubscribe function.
 *
 * No autoplay of the next track on ended, keep it simple.
 * ------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var EVENTS = ['time', 'state', 'error'];
  var listeners = { time: [], state: [], error: [] };

  var el = new Audio();
  el.preload = 'auto';

  var currentRelease = null;
  var currentTrackIndex = null;
  var epoch = 0; // guards rapid loadAndPlay calls, only the newest may set src/play

  function emit(event, payload) {
    var cbs = listeners[event] || [];
    for (var i = 0; i < cbs.length; i++) {
      try { cbs[i](payload); } catch (e) { /* a broken listener must not kill playback */ }
    }
  }

  function statePayload(playing) {
    return { playing: playing, release: currentRelease, trackIndex: currentTrackIndex };
  }

  function hasDirectAudio(release) {
    if (!release) return false;
    if (release.audioUrl) return true;
    return Array.isArray(release.tracks) && release.tracks.some(function (t) { return t && t.audioUrl; });
  }

  function resetElement() {
    try { el.pause(); } catch (e) { /* noop */ }
    el.removeAttribute('src');
    try { el.load(); } catch (e) { /* noop */ }
  }

  // ---- element wiring -------------------------------------------------
  el.addEventListener('timeupdate', function () {
    var duration = isFinite(el.duration) ? el.duration : 0;
    var current = el.currentTime || 0;
    emit('time', { current: current, duration: duration, ratio: duration > 0 ? current / duration : 0 });
  });

  el.addEventListener('play', function () {
    emit('state', statePayload(true));
  });

  el.addEventListener('pause', function () {
    emit('state', statePayload(false));
  });

  el.addEventListener('ended', function () {
    // no autoplay of the next track; keep release loaded so the mini player
    // and play states still make sense
    emit('state', statePayload(false));
  });

  el.addEventListener('loadedmetadata', function () {
    emit('state', statePayload(!el.paused));
  });

  el.addEventListener('error', function () {
    if (!currentRelease) return; // error from a cleared src (stop/reset), ignore
    var release = currentRelease;
    currentRelease = null;
    currentTrackIndex = null;
    resetElement();
    emit('error', { release: release, message: 'Audio preview failed to load. Listen on Bandcamp instead.' });
    emit('state', statePayload(false));
  });

  // ---- public API ------------------------------------------------------
  function loadAndPlay(release, trackIndex) {
    return new Promise(function (resolve) {
      // No direct URL anywhere -> never touch the <audio> element.
      if (!hasDirectAudio(release)) { resolve({ ok: false }); return; }

      var myEpoch = ++epoch;
      var tracks = Array.isArray(release.tracks) ? release.tracks : [];
      var idx = trackIndex == null ? 0 : trackIndex;

      var url = null;
      var playIndex = idx;
      if (release.audioUrl) {
        url = release.audioUrl;
      } else if (tracks[idx] && tracks[idx].audioUrl) {
        url = tracks[idx].audioUrl;
      } else {
        for (var i = 0; i < tracks.length; i++) {
          if (tracks[i] && tracks[i].audioUrl) { url = tracks[i].audioUrl; playIndex = i; break; }
        }
      }
      if (!url) { resolve({ ok: false }); return; }

      currentRelease = release;
      currentTrackIndex = playIndex;
      el.src = url; // setting src cancels any in-flight load from a previous call

      var p = el.play();
      if (p && typeof p.catch === 'function') {
        p.then(function () {
          resolve({ ok: myEpoch === epoch });
        }).catch(function () {
          if (myEpoch !== epoch) { resolve({ ok: false }); return; } // superseded by a newer call
          currentRelease = null;
          currentTrackIndex = null;
          emit('error', { release: release, message: 'Playback was blocked or failed. Listen on Bandcamp instead.' });
          emit('state', statePayload(false));
          resolve({ ok: false });
        });
      } else {
        resolve({ ok: true });
      }
    });
  }

  function toggle() {
    if (!currentRelease) return; // no-op when nothing is loaded
    if (el.paused) {
      var p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          emit('error', { release: currentRelease, message: 'Playback was blocked by the browser.' });
        });
      }
    } else {
      el.pause();
    }
  }

  function seekRatio(ratio) {
    if (!currentRelease) return;
    if (!isFinite(el.duration) || el.duration <= 0) return;
    var r = Number(ratio);
    if (!isFinite(r)) return;
    r = Math.min(1, Math.max(0, r));
    try { el.currentTime = r * el.duration; } catch (e) { /* not seekable yet */ }
  }

  function stop() {
    epoch++; // cancel any in-flight loadAndPlay
    currentRelease = null;
    currentTrackIndex = null;
    resetElement();
    emit('state', statePayload(false));
  }

  function on(event, cb) {
    if (EVENTS.indexOf(event) === -1 || typeof cb !== 'function') return function () {};
    listeners[event].push(cb);
    return function () {
      listeners[event] = listeners[event].filter(function (fn) { return fn !== cb; });
    };
  }

  window.FlexAudio = {
    loadAndPlay: loadAndPlay,
    toggle: toggle,
    seekRatio: seekRatio,
    stop: stop,
    on: on
  };
})();
